<!-- The 1024px below is a FIXED width on purpose — do NOT relax it to a max-width utility (writing the
     literal class name here would make Tailwind emit a dead rule, hence the prose). The host is
     shrink-to-fit (create-eviction.component.scss: `:host-context { margin: 0 auto }` gives it an auto
     cross-axis margin, which defeats `align-self: stretch`), so this child's definite width is what
     sizes the whole wizard. With only a max-width, `w-full` resolves against a content-sized ancestor
     and the column collapses to ~808px, which also drops the field grid from 4 columns to 3.
     The one place it must be fluid — the 760px send-to-attorney slide-in — overrides it via
     `.intake-slide-in-panel .wizard-wrapper` in ee-portal/src/styles.scss. -->
<div class="wizard-wrapper w-full h-full flex flex-col flex-1 md:w-[1024px] justify-start items-stretch">
  @if (loading) {
    <div class="loading-overlay">
      <ee-loading message="Loading..."></ee-loading>
    </div>
  } @else {
    <mat-stepper
      #stepper
      [orientation]="smallScreen ? 'vertical' : 'horizontal'"
      class="wizard no-scroll-y"
      [linear]="true"
      (selectionChange)="stepChanged()">
      <!-- Property-linked PM submissions skip the Contact/Business step entirely: the server
           synthesizes client/user details from the linked property + submitting PM user. -->
      @if ((forceIntakeForm && !skipClientStep) || loggedInOrg?.type === attorneyAccount) {
        <mat-step [stepControl]="intakeEvictionFormService.workflowDetails">
          <ng-template matStepLabel>{{ firstStepLabel }}</ng-template>
          <ng-template matStepContent>
            <form [formGroup]="intakeEvictionFormService.workflowDetails" class="flex flex-1 flex-col">
              @if (!forceIntakeForm && loggedInOrg) {
                <ee-workflow-form
                  class="mb-4"
                  [preSelectedClientId]="selectedClientId"
                  [cloneCase]="cloneCase"
                  (addClient)="addClient.emit($event)"
                  (workflowSelected)="updateSelectedWorkflow($event)"></ee-workflow-form>
              }
              @if (forceIntakeForm) {
                <div class="flex flex-col justify-stretch items-stretch mb-4">
                  <ee-expansion-panel
                    class="mb-4"
                    header="Contact Information"
                    [expanded]="true"
                    [collapsible]="false"
                    [fullWidth]="true">
                    <div class="flex flex-row" ee-expansion-panel-header-actions>
                      <div style="font-size: 14px">
                        {{ intakeEvictionFormService.getSectionDescription(intakeFormSections.USER) }}
                      </div>
                    </div>
                    <ee-user-form
                      [form]="$any(intakeEvictionFormService.workflowDetails.get('user_details'))"></ee-user-form>
                  </ee-expansion-panel>

                  <ee-expansion-panel
                    header="Business Information"
                    [expanded]="true"
                    [collapsible]="false"
                    [fullWidth]="true">
                    <div class="flex flex-row" ee-expansion-panel-header-actions>
                      <div style="font-size: 14px">
                        {{ intakeEvictionFormService.getSectionDescription(intakeFormSections.ORGANIZATION) }}
                      </div>
                    </div>
                    <ee-organization-form
                      [form]="$any(intakeEvictionFormService.workflowDetails.get('client_details'))"
                      [hideBillingFields]="true"></ee-organization-form>
                  </ee-expansion-panel>
                </div>
              }
              <div class="actions flex flex-row justify-end items-start">
                <button
                  type="button"
                  mat-flat-button
                  color="accent"
                  (click)="goForward(1, stepper)"
                  [disabled]="$safeNavigationMigration(intakeEvictionFormService.workflowDetails?.invalid)"
                  data-tracking-event="create-eviction-step-1-next">
                  Next
                </button>
              </div>
            </form>
          </ng-template>
        </mat-step>
      }
      <mat-step [stepControl]="intakeEvictionFormService.propertyDetails">
        <ng-template matStepLabel>Tenant & Property Details</ng-template>
        <ng-template matStepContent>
          @if (intakeEvictionFormService.propertyDetails && intakeEvictionFormService.intakeForm) {
            <div class="form-wrapper flex flex-1 flex-col">
              <ee-eviction-property-details
                class="mb-4"
                [options]="intakeEvictionFormService.options"
                [loggedInOrg]="loggedInOrg"
                [loggedInUser]="loggedInUser"
                [allowCourtSelection]="attorneySelectsCourt"
                [plaintiffOptions]="intakeEvictionFormService.plaintiffOptions"
                [selectedAttorneyId]="intakeEvictionFormService.attorneyId"
                [intakeForm]="intakeEvictionFormService.intakeForm"
                [selectedClient]="
                  $any(
                    $safeNavigationMigration(intakeEvictionFormService.workflowDetails?.get('client_details')?.value)
                  )
                "
                [tenantDetailsForm]="intakeEvictionFormService.tenantDetails"
                [tenantSection]="intakeEvictionFormService.tenantsSection"
                [propertyDetailsForm]="intakeEvictionFormService.propertyDetails"
                [propertySection]="intakeEvictionFormService.propertySection"
                [forceMultiSelectReasons]="intakeEvictionFormService.forceMultiSelectReasons"
                [intakeEvictionFormService]="intakeEvictionFormService"></ee-eviction-property-details>
              <div
                class="actions flex flex-row items-start"
                [ngClass]="{
                  'justify-end': loggedInOrg?.type !== attorneyAccount,
                  'justify-between': (loggedInOrg?.type === attorneyAccount || forceIntakeForm) && !skipClientStep
                }">
                <!-- No Back when the Contact/Business step is skipped: this IS the first step, and
                     goBack(2) would tear down the section forms (removeForms). -->
                @if ((loggedInOrg?.type === attorneyAccount || forceIntakeForm) && !skipClientStep) {
                  <button
                    mat-flat-button
                    (click)="goBack(2, stepper)"
                    type="button"
                    data-tracking-event="create-eviction-step-2-back">
                    Back
                  </button>
                }
                <button
                  mat-flat-button
                  color="accent"
                  type="button"
                  (click)="goForward(3, stepper)"
                  data-tracking-event="create-eviction-step-2-next">
                  Next
                </button>
              </div>
            </div>
          }
        </ng-template>
      </mat-step>
      <mat-step [stepControl]="intakeEvictionFormService.claimDetails">
        <ng-template matStepLabel>Claims & Lease Terms</ng-template>
        <ng-template matStepContent>
          @if (intakeEvictionFormService.claimDetails && intakeEvictionFormService.intakeForm) {
            <div class="form-wrapper flex-1 flex flex-col">
              <ee-expansion-panel
                class="mb-4"
                header="Claims"
                [expanded]="true"
                [collapsible]="false"
                [fullWidth]="true">
                <div class="flex flex-row" ee-expansion-panel-header-actions>
                  <div style="font-size: 14px">
                    {{ intakeEvictionFormService.getSectionDescription(intakeFormSections.CLAIMS) }}
                  </div>
                </div>
                <ee-claims-section
                  [form]="intakeEvictionFormService.claimDetails"
                  [claimsSection]="intakeEvictionFormService.claimsSection"
                  [clearOnSwitch]="true"
                  [overrideFieldVisibility]="loggedInOrg?.type === attorneyAccount"
                  [options]="intakeEvictionFormService.options"
                  [evictionReason]="intakeEvictionFormService.evictionReason"
                  [eviction]="intakeEvictionFormService.evictionDetails"></ee-claims-section>
              </ee-expansion-panel>
              <ee-expansion-panel
                class="mb-4"
                header="Lease Terms"
                [expanded]="true"
                [collapsible]="false"
                [fullWidth]="true">
                <div class="flex flex-row" ee-expansion-panel-header-actions>
                  <div style="font-size: 14px">
                    {{ intakeEvictionFormService.getSectionDescription(intakeFormSections.LEASE_TERMS) }}
                  </div>
                </div>
                <ee-lease-terms-section
                  [form]="intakeEvictionFormService.leaseTermsDetails"
                  [leaseTermsSection]="intakeEvictionFormService.leaseTermsSection"
                  [overrideFieldVisibility]="loggedInOrg?.type === attorneyAccount"
                  [options]="intakeEvictionFormService.options"></ee-lease-terms-section>
              </ee-expansion-panel>
              @if (!!intakeEvictionFormService.internalDetails && intakeEvictionFormService.setupInternalFields) {
                <ee-expansion-panel
                  class="mb-4"
                  header="Internal Fields"
                  [expanded]="true"
                  [collapsible]="false"
                  [fullWidth]="true">
                  <div class="flex flex-row" ee-expansion-panel-header-actions>
                    <div style="font-size: 14px">
                      {{ intakeEvictionFormService.getSectionDescription(intakeFormSections.INTERNAL) }}
                    </div>
                  </div>
                  <ee-internal-section
                    [form]="intakeEvictionFormService.internalDetails"
                    [internalSection]="intakeEvictionFormService.internalSection"
                    [options]="intakeEvictionFormService.options"></ee-internal-section>
                </ee-expansion-panel>
              }
              <div class="actions flex flex-row justify-between items-start">
                <button
                  mat-flat-button
                  (click)="goBack(3, stepper)"
                  type="button"
                  data-tracking-event="create-eviction-step-3-back">
                  Back
                </button>
                <button
                  mat-flat-button
                  color="accent"
                  type="button"
                  (click)="goForward(4, stepper)"
                  data-tracking-event="create-eviction-step-3-next">
                  Next
                </button>
              </div>
            </div>
          }
        </ng-template>
      </mat-step>
      <mat-step [stepControl]="intakeEvictionFormService.documents">
        <ng-template matStepLabel>Documentation</ng-template>
        <ng-template matStepContent>
          @if (currentStep === 4 && intakeEvictionFormService.documents && intakeEvictionFormService.intakeForm) {
            <div class="form-wrapper flex flex-1 flex-col">
              <div class="mb-4 flex flex-col flex-[1_1_100%]">
                @if (
                  cloneCase &&
                  intakeEvictionFormService.existingDocuments &&
                  intakeEvictionFormService.existingDocuments.length
                ) {
                  <ee-expansion-panel
                    class="mb-4"
                    header="Existing Documents"
                    [expanded]="true"
                    [collapsible]="false"
                    [fullWidth]="true">
                    <div class="flex flex-row" ee-expansion-panel-header-actions>
                      <div style="font-size: 14px">
                        {{ intakeEvictionFormService.getSectionDescription(intakeFormSections.DOCUMENTS) }}
                      </div>
                    </div>
                    <div class="flex flex-col justify-start items-stretch">
                      <div class="file-scroller-wrapper">
                        @for (
                          attachment of intakeEvictionFormService.existingDocuments.controls;
                          track attachment;
                          let i = $index
                        ) {
                          <ee-intake-doc-file
                            [caseId]="$safeNavigationMigration(cloneCase?.case_id)"
                            [attachment]="attachment.value"
                            [removable]="true"
                            (remove)="intakeEvictionFormService.removeExistingDocument(i)"></ee-intake-doc-file>
                        }
                      </div>
                    </div>
                  </ee-expansion-panel>
                }
                @if (
                  currentStep === 4 &&
                  intakeEvictionFormService.documents &&
                  intakeEvictionFormService.documentSection &&
                  intakeEvictionFormService.claimDetails &&
                  intakeEvictionFormService.propertyDetails
                ) {
                  <ee-eviction-documentation
                    class="mb-4"
                    [currentStep]="currentStep"
                    [existingDocumentsForm]="intakeEvictionFormService.existingDocuments"
                    [documentsForm]="intakeEvictionFormService.documents"
                    [section]="intakeEvictionFormService.documentSection"
                    [workflowForm]="intakeEvictionFormService.workflowDetails"
                    [claimForm]="intakeEvictionFormService.claimDetails"
                    [propertyForm]="intakeEvictionFormService.propertyDetails"
                    [eviction]="intakeEvictionFormService.evictionDetails"
                    [attorney_org_id]="
                      forceIntakeForm && !authenticatedUpload ? intakeEvictionFormService.attorneyId : null
                    "
                    [isPublicIntakeForm]="forceIntakeForm && !authenticatedUpload"
                    (uploadsChange)="onUploadsChange($event)"
                    (uploadsReadyChange)="onUploadsReadyChange($event)"></ee-eviction-documentation>
                }
                @if (loggedInOrg?.type !== attorneyAccount) {
                  <ee-expansion-panel
                    class="mb-4"
                    header="Notes"
                    [expanded]="true"
                    [collapsible]="false"
                    [fullWidth]="true">
                    <div
                      class="flex flex-row justify-stretch items-stretch"
                      [formGroup]="intakeEvictionFormService.splitForm">
                      <mat-form-field class="hide-label-gap flex-1" appearance="fill" floatLabel="auto">
                        <textarea matInput rows="4" style="font-size: 16px" formControlName="note"></textarea>
                        <mat-hint>Notes for the attorney</mat-hint>
                      </mat-form-field>
                    </div>
                  </ee-expansion-panel>
                }
                @if (loggedInOrg?.type === attorneyAccount && selectedWorkflow) {
                  <ee-expansion-panel
                    class="mb-4"
                    [header]="selectedWorkflow.name + ': Intake Fields'"
                    [expanded]="true"
                    [collapsible]="false"
                    [fullWidth]="true">
                    <div class="w-full flex flex-1 flex-col justify-stretch items-stretch">
                      @if (intakeEvictionFormService.workflowDetails && intakeEvictionFormService.intakeStep) {
                        <ee-complete-workflow-step
                          class="flex-1 w-full"
                          [caseSettings]="caseSettings"
                          [nextStep]="intakeEvictionFormService.intakeStep"
                          [clientId]="
                            $safeNavigationMigration(
                              intakeEvictionFormService.workflowDetails?.get('client_details')?.value?.id
                            )
                          "
                          [county]="
                            $safeNavigationMigration(intakeEvictionFormService.propertyDetails?.get('county')?.value)
                          "
                          [userSuggestions]="intakeEvictionFormService.userSuggestions"
                          [tenants]="intakeEvictionFormService.tenants"
                          [planAllowsCaseTasks]="false"
                          [parentForm]="intakeEvictionFormService.splitForm"
                          [hideLabel]="true"
                          [forceVertical]="true" />
                      }
                    </div>
                  </ee-expansion-panel>
                }
              </div>
              <div class="actions flex flex-row justify-between items-start">
                <button
                  mat-flat-button
                  (click)="goBack(4, stepper)"
                  type="button"
                  data-tracking-event="create-eviction-step-4-back">
                  Back
                </button>
                <button
                  mat-flat-button
                  type="button"
                  color="accent"
                  [loading]="saving"
                  (click)="save()"
                  matStepperNext
                  [disabled]="saving"
                  data-tracking-event="submit-case">
                  {{
                    !forceIntakeForm &&
                    (loggedInOrg?.type === attorneyAccount || loggedInOrg?.id === intakeEvictionFormService.attorneyId)
                      ? "Save"
                      : "Send"
                  }}
                </button>
              </div>
            </div>
          }
        </ng-template>
      </mat-step>
    </mat-stepper>
  }
</div>
